import { getAuthenticatedUser } from 'components/auth'
import { useCallback, useEffect, useState } from 'react'
import { getAITOSAcceptance, updateAITOSAcceptance } from '../api/Users'

const AI_TOS_ACCEPTANCE_KEY = 'ai-tos-acceptance'

let _pending = false

/**
 * useUserHasAcceptedTOS - Hook to check if the user has accepted the TOS
 * @param {Object} user - The user object
 */
const useUserHasAcceptedAITOS = () => {
  const user = getAuthenticatedUser()

  const [accepted, _setAccepted] = useState(false)
  const [didCheck, setDidCheck] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Helper function to set the accepted state and local storage, and update the API
   * @param {boolean} value - The value to set
   * @param {boolean} save - Whether or not to save the value to the API
   * @returns {Promise<Object>}
   */
  const setAccepted = async (value, save = true) => {
    _setAccepted(value)
    localStorage.setItem(AI_TOS_ACCEPTANCE_KEY, value ? 'true' : 'false')

    const saved = save ? await updateAITOSAcceptance(value) : true

    return saved
  }

  /**
   * Helper function to check if the user has accepted the TOS
   * @returns {Promise<void>}
   */
  const checkTOSAcceptance = useCallback(async () => {
    // Return early if we are already checking
    if (_pending) return

    // Set the pending flag
    _pending = true

    // Start by checking local storage
    const localAccepted = localStorage.getItem(AI_TOS_ACCEPTANCE_KEY)

    // If the user has accepted the TOS, set the state and return
    if (accepted || localAccepted === 'true') {
      setAccepted(true, false)
      _pending = false
      return
    }

    // Fetch the user's acceptance status from the API
    const status = await getAITOSAcceptance()

    // Check for errors
    if (status.error || status.detail) {
      setError(status.error || status.detail)
    } else {
      const didAccept = !!status.ai_tos_accepted_on
      // Set the state
      setAccepted(didAccept, false)
    }

    _pending = false
  }, [accepted])

  useEffect(() => {
    // Return early if we are not authenticated
    if (!user || didCheck || loading) return

    // Check the TOS acceptance
    async function checkAcceptance() {
      // Set the loading state
      setLoading(true)
      setDidCheck(true)
      await checkTOSAcceptance()

      // Set the loading state
      setLoading(false)
    }

    checkAcceptance()
  }, [loading, didCheck, user, checkTOSAcceptance])

  return {
    accepted,
    setAccepted,
    loading,
    error
  }
}

export default useUserHasAcceptedAITOS
